import { ref } from 'vue'
import { useApprovalFlowStore } from '@/pinia-store/useApprovalFlowStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { apiLayersToGroup, ensureMandatoryRoles } from '@/utils/approval-flow-transformers'
import type { ApprovalGroup } from '@/types/approval-flow.types'
import type { ApprovalFlowTemplateDetail } from '@/Network/Types/Responses/ApprovalFlowTemplateResponses'

const IMPORT_FALLBACK = 'Não foi possível importar a configuração de excepcionalidade.'

/**
 * Falha de validação local do import (nada a importar). Separada do erro de
 * API para o usuário ler o motivo exato — antes as duas caíam no mesmo toast
 * genérico, e "Modelo não encontrado." nunca aparecia.
 */
class ImportValidationError extends Error {}

export function useImportApprovalFlow() {
  const { notifyError, notifyErrorMessage } = useErrorToast()
  const approvalFlowStore = useApprovalFlowStore()
  const isImporting = ref(false)

  function findTemplateById(templateId: number): ApprovalFlowTemplateDetail | undefined {
    const direct = approvalFlowStore.templates.find((t) => t.id === templateId)
    if (direct) return direct

    for (const group of approvalFlowStore.groupedTemplates) {
      for (const schedule of group.schedules) {
        const found = schedule.templates.find((t) => t.id === templateId)
        if (found) return found
      }
    }

    return undefined
  }

  function importFromTemplate(templateId: number): ApprovalGroup {
    const template = findTemplateById(templateId)
    if (!template) {
      throw new ImportValidationError('Modelo não encontrado.')
    }
    return ensureMandatoryRoles(apiLayersToGroup(template.layers))
  }

  async function importFromSchedule(scheduleId: number): Promise<ApprovalGroup> {
    const flow = await approvalFlowStore.fetchFlowByScheduleIdSilent(scheduleId)
    if (!flow) {
      throw new ImportValidationError(
        'Nenhuma configuração de excepcionalidade encontrada para essa escala.'
      )
    }
    return ensureMandatoryRoles(apiLayersToGroup(flow.layers))
  }

  async function importConfig(payload: {
    source: 'template' | 'scale'
    id: number
  }): Promise<ApprovalGroup | null> {
    isImporting.value = true

    try {
      if (payload.source === 'template') {
        return importFromTemplate(payload.id)
      } else {
        return await importFromSchedule(payload.id)
      }
    } catch (error) {
      if (error instanceof ImportValidationError) {
        notifyErrorMessage(error.message)
      } else {
        notifyError(error, IMPORT_FALLBACK)
      }
      return null
    } finally {
      isImporting.value = false
    }
  }

  return { importConfig, isImporting }
}
